import React, { useMemo } from 'react';
import type { RouteOperationalProcessesPayload } from '../../operationalDomain/processes/operationalProcessTypes';
import {
  pickGovernanceRequired,
  pickTopRecommendations
} from '../../operationalDomain/decisionSupport/decisionSupportTypes';
import { operationalDepartmentLabel } from '../../operationalDomain/operationalLabels';

export type ExecutionRecommendationStripProps = {
  payload: RouteOperationalProcessesPayload | null | undefined;
};

function governanceStateOf(
  r: { governanceState?: string; metadata?: Record<string, unknown> }
): string | undefined {
  return (
    r.governanceState ??
    (typeof r.metadata?.governanceState === 'string' ? r.metadata.governanceState : undefined)
  );
}

const ExecutionRecommendationStrip: React.FC<ExecutionRecommendationStripProps> = ({ payload }) => {
  const ds = payload?.decisionSupport ?? null;

  const mandatory = useMemo(() => pickGovernanceRequired(ds), [ds]);
  const recommendations = useMemo(() => pickTopRecommendations(ds), [ds]);

  const deptPressure = useMemo(
    () =>
      (ds?.capacityPressure ?? []).filter(
        (c) => c.escalationRisk || c.pressureLevel === 'high' || c.pressureLevel === 'critical'
      ),
    [ds?.capacityPressure]
  );

  const optionalRecs = useMemo(
    () =>
      recommendations.filter((r) => governanceStateOf(r) !== 'required'),
    [recommendations]
  );

  if (!mandatory.length && !optionalRecs.length && !deptPressure.length) {
    return null;
  }

  return (
    <section className="execution-workspace__panel execution-workspace__panel--recommendations">
      {mandatory.length > 0 ? (
        <>
          <div className="execution-workspace__panel-title execution-workspace__panel-title--required">
            Требуется действие
          </div>
          <ul className="execution-recommendation-list">
            {mandatory.map((r) => (
              <li
                key={r.key}
                className="execution-recommendation-list__item execution-recommendation-list__item--required"
              >
                <span className="execution-recommendation-list__title">{r.title}</span>
                {r.reason ? (
                  <span className="execution-recommendation-list__reason">{r.reason}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {optionalRecs.length > 0 ? (
        <>
          <div className="execution-workspace__panel-title">Рекомендации</div>
          <ul className="execution-recommendation-list">
            {optionalRecs.map((r) => (
              <li
                key={r.key}
                className={`execution-recommendation-list__item execution-recommendation-list__item--${r.priority.toLowerCase()}`}
              >
                <span className="execution-recommendation-list__title">{r.title}</span>
                {r.reason ? (
                  <span className="execution-recommendation-list__reason">{r.reason}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {deptPressure.map((d) => (
        <div key={d.department} className="execution-recommendation-list__dept">
          {operationalDepartmentLabel(d.department)}: {d.label}
        </div>
      ))}
    </section>
  );
};

export default ExecutionRecommendationStrip;
